import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Tooltip } from "antd";
import Heading from "./Heading";
import InfoBlock from "./InfoBlock";
import { BoxDetailWrapper } from "../style";
import { UnderlyingContent, SectionMediumDivider } from "../../../style";
import PurposeSummary from "../../../../../Components/PurposeSummary/PurposeSummary";
import { addressFormat } from "../../../utils";
import getRecognitionRender from "./ViewRecognition";

export const getPurposeComments = (purpose, purposeId) =>
  purpose.additionalComments ||
  purpose.projectName ||
  purpose.eventName ||
  purpose.missionaryName ||
  purpose.missionaryLocation ? (
    <>
      <Tooltip
        className="grantsFlow"
        placement="bottomLeft"
        trigger={window.innerWidth < 992 ? "click" : "hover"}
        title={() => <PurposeSummary {...purpose} />}
      >
        <UnderlyingContent id={`${purposeId}anrPurposeName`}>
          {purpose.purposeName}
        </UnderlyingContent>
      </Tooltip>
    </>
  ) : (
    <>
      <Tooltip
        className="grantsFlow"
        placement="bottomLeft"
        trigger={window.innerWidth < 992 ? "click" : "hover"}
        title={() => <PurposeSummary {...purpose} />}
      >
        <UnderlyingContent id={`${purposeId}anrGrantsFlowPurposeName`}>
          {purpose.purposeName}
        </UnderlyingContent>
      </Tooltip>
    </>
  );

export const getAdditionalAttrRender = (purposeRecognition) => {
  const twoSections =
    purposeRecognition.notifyName &&
    (purposeRecognition.inMemoryOfName || purposeRecognition.inHonorOfName);
  return (
    <>
      {purposeRecognition.notifyName && (
        <Col span={twoSections ? 12 : 24}>
          <InfoBlock
            title="Please Notify"
            breakAll
            value={purposeRecognition.notifyName}
            confirmationPage
          />
        </Col>
      )}
      {(purposeRecognition.inMemoryOfName ||
        purposeRecognition.inHonorOfName) && (
        <Col span={twoSections ? 11 : 24} offset={twoSections && 1}>
          <InfoBlock
            title="Additional Attribution"
            breakAll
            value={purposeRecognition.inMemoryOfName}
            value2={purposeRecognition.inHonorOfName}
            confirmationPage
          />
        </Col>
      )}
    </>
  );
};

/**
 * Components layout is complicated because of the design requirements
 */
export default function Step1Summary({
  charityInfo = {},
  purposeRecognition = {},
}) {
  return (
    <Row>
      <Col xs={0} md={0} lg={24}>
        <Row>
          <Col span={8}>
            <Heading
              title={charityInfo.title}
              edit={charityInfo.edit}
              handleEdit={charityInfo.handleEdit}
              id="btnCharityInfoEdit1"
            />
          </Col>
          <Col span={14} offset={2}>
            <Heading
              title={purposeRecognition.title}
              edit={purposeRecognition.edit}
              handleEdit={purposeRecognition.handleEdit}
              id="btnpurposeRecognitionEdit"
            />
          </Col>
        </Row>
        <Row type="flex">
          <BoxDetailWrapper border span={8}>
            <InfoBlock
              border
              title="Full charity name"
              value={charityInfo.charityName}
            />
          </BoxDetailWrapper>
          <BoxDetailWrapper border span={14} offset={2}>
            <InfoBlock
              border
              title="Purpose"
              value={getPurposeComments(purposeRecognition, 'charityName')}
            />
          </BoxDetailWrapper>
        </Row>
        <Row type="flex">
          <BoxDetailWrapper border span={8}>
            <InfoBlock
              border
              title="Address"
              value={addressFormat(charityInfo.charityAddress)}
            />
          </BoxDetailWrapper>
          <BoxDetailWrapper border span={14} offset={2}>
            <InfoBlock
              border
              title="Grant Recognition"
              value={getRecognitionRender(
                purposeRecognition.grantRecognition,
                purposeRecognition.advisors,
                purposeRecognition.thirdParty,
                purposeRecognition.includeAdvisorInfo
              )}
            />
          </BoxDetailWrapper>
        </Row>
        <Row type="flex">
          <BoxDetailWrapper span={8}>
            <InfoBlock title="EIN" value={charityInfo.charityEin} />
          </BoxDetailWrapper>
          <BoxDetailWrapper span={14} offset={2}>
            {getAdditionalAttrRender(purposeRecognition)}
          </BoxDetailWrapper>
        </Row>
      </Col>
      <Col md={24} xs={24} lg={0}>
        <Heading
          title={charityInfo.title}
          edit={charityInfo.edit}
          handleEdit={charityInfo.handleEdit}
          id="btnCharityInfoEdit2"
        />
        <BoxDetailWrapper border>
          <InfoBlock
            border
            title="Full Charity Name"
            value={charityInfo.charityName}
          />
        </BoxDetailWrapper>
        <BoxDetailWrapper border>
          <InfoBlock
            border
            title="Address"
            value={addressFormat(charityInfo.charityAddress)}
          />
        </BoxDetailWrapper>
        <BoxDetailWrapper>
          <InfoBlock title="EIN" value={charityInfo.charityEin} />
        </BoxDetailWrapper>
        <SectionMediumDivider />
        <Heading
          title={purposeRecognition.title}
          edit={purposeRecognition.edit}
          handleEdit={purposeRecognition.handleEdit}
          id="btnpurposeRecognitionEdit2"
        />
        <BoxDetailWrapper border>
          <InfoBlock
            border
            title="Purpose"
            value={getPurposeComments(purposeRecognition, 'ein')}
          />
        </BoxDetailWrapper>
        <BoxDetailWrapper
          border={
            purposeRecognition.notifyName ||
            purposeRecognition.inMemoryOfName ||
            purposeRecognition.inHonorOfName
          }
        >
          <InfoBlock
            border
            title="Grant Recognition"
            value={getRecognitionRender(
              purposeRecognition.grantRecognition,
              purposeRecognition.advisors,
              purposeRecognition.thirdParty,
              purposeRecognition.includeAdvisorInfo
            )}
          />
        </BoxDetailWrapper>
        {(purposeRecognition.notifyName ||
          purposeRecognition.inMemoryOfName ||
          purposeRecognition.inHonorOfName) && (
          <BoxDetailWrapper>{getAdditionalAttrRender(purposeRecognition)}</BoxDetailWrapper>
        )}
      </Col>
    </Row>
  );
}

Step1Summary.propTypes = {
  charityInfo: PropTypes.shape({
    charityAddress: PropTypes.shape({
      addressLine1: PropTypes.string,
      addressLine2: PropTypes.string,
      addressLine3: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
    }),
    charityEin: PropTypes.string,
    charityName: PropTypes.string,
    title: PropTypes.string,
  }),
  purposeRecognition: PropTypes.shape({
    accountName: PropTypes.string,
    additionalComments: PropTypes.string,
    advisors: PropTypes.array,
    eventName: PropTypes.string,
    grantRecognition: PropTypes.string,
    inHonorOfName: PropTypes.string,
    inMemoryOfName: PropTypes.string,
    includeAdvisorInfo: PropTypes.bool,
    missionaryLocation: PropTypes.string,
    missionaryName: PropTypes.string,
    notifyName: PropTypes.any,
    projectName: PropTypes.string,
    purposeName: PropTypes.string,
    thirdParty: PropTypes.string,
    title: PropTypes.string,
  }),
};
